<!--
  Confined Space Alternate Entry Form
  
  Step-by-step confined space permit with:
  - 4 steps: Basic Info, Hazards, Ventilation & Monitoring, Additional Info
  - Dynamic hazard fields (up to 5 physical, 2 atmospheric) with add/remove buttons
  - Multiple air monitoring results (oxygen, H2S, CO, LEL) with expandable fields
  - Conditional ventilation type selection
  - Signature requirement on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div *ngIf="form" formContent [formGroup]="form" class="form-content">
    <!-- Basic Information Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
      <ion-list>
        <!-- Basic Entry Information Section -->
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('spaceLocation')!"
                [label]="fieldMappings['spaceLocation']"
                [requiredField]="requiredFields['spaceLocation']"
                placeholder="Enter location of the space"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('entryDate')!"
                [label]="fieldMappings['entryDate']"
                [requiredField]="requiredFields['entryDate']"
                [today]="true"
              >
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('entryDuration')!"
                [label]="fieldMappings['entryDuration']"
                [requiredField]="requiredFields['entryDuration']"
                placeholder="Enter entry duration (e.g., 2 hours)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- List of Entrants Section -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{ steps[0].sections?.[1]?.title }}</h2>
            <p>{{ steps[0].sections?.[1]?.description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('entrant1')!"
                [label]="fieldMappings['entrant1']"
                [requiredField]="requiredFields['entrant1']"
                placeholder="Enter first entrant name"
              >
              </app-text-input>
            </ion-col>

            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="basicFormGroup"
                [control]="basicFormGroup.get('entrant2')!"
                [label]="fieldMappings['entrant2']"
                [requiredField]="requiredFields['entrant2']"
                placeholder="Enter second entrant name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Hazards Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
      <ion-list>
        <!-- Hazards Section -->
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <!-- Physical Hazards -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[1].sections?.[0]?.title }}</h2>
              <p>{{ steps[1].sections?.[0]?.description }}</p>
            </ion-label>
          </ion-item-divider>
          <!-- Physical Hazard 1 -->

          <ion-row
            [class]="showPhysicalHazardIndex[1] ? '' : 'ion-no-padding ion-no-margin'"
          >
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('physicalHazard1')!"
                [label]="fieldMappings['physicalHazard1']"
                [requiredField]="requiredFields['physicalHazard1']"
                placeholder="Enter first physical hazard"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('physicalHazardAction1')!"
                [label]="fieldMappings['physicalHazardAction1']"
                [requiredField]="requiredFields['physicalHazardAction1']"
                placeholder="Enter action taken"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="
                  hazardsFormGroup.get('physicalHazardActionDescription1')!
                "
                [label]="fieldMappings['physicalHazardActionDescription1']"
                [requiredField]="
                  requiredFields['physicalHazardActionDescription1']
                "
                placeholder="Enter action description"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Physical Hazard 2 -->
          <ng-container *ngIf="showPhysicalHazardIndex[1]">
            <ion-item-divider class="form-subsection-title">
              <ion-label>
                <p>{{ steps[1].sections?.[1]?.subtitle }}</p>
              </ion-label>
            </ion-item-divider>

            <ion-row
              [class]="
                showPhysicalHazardIndex[2] ? '':'ion-no-padding ion-no-margin'
              "
            >
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazard2')!"
                  [label]="fieldMappings['physicalHazard2']"
                  [requiredField]="requiredFields['physicalHazard2']"
                  placeholder="Enter physical hazard"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazardAction2')!"
                  [label]="fieldMappings['physicalHazardAction2']"
                  [requiredField]="requiredFields['physicalHazardAction2']"
                  placeholder="Enter action taken"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="
                    hazardsFormGroup.get('physicalHazardActionDescription2')!
                  "
                  [label]="fieldMappings['physicalHazardActionDescription2']"
                  [requiredField]="
                    requiredFields['physicalHazardActionDescription2']
                  "
                  placeholder="Enter action description"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>

          <!-- Physical Hazard 3 -->
          <ng-container *ngIf="showPhysicalHazardIndex[2]">
            <ion-item-divider class="form-subsection-title">
              <ion-label>
                <p>{{ steps[1].sections?.[2]?.subtitle }}</p>
              </ion-label>
            </ion-item-divider>

            <ion-row
              [class]="
                showPhysicalHazardIndex[3] ? '' : 'ion-no-padding ion-no-margin'
              "
            >
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazard3')!"
                  [label]="fieldMappings['physicalHazard3']"
                  [requiredField]="requiredFields['physicalHazard3']"
                  placeholder="Enter physical hazard"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazardAction3')!"
                  [label]="fieldMappings['physicalHazardAction3']"
                  [requiredField]="requiredFields['physicalHazardAction3']"
                  placeholder="Enter action taken"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="
                    hazardsFormGroup.get('physicalHazardActionDescription3')!
                  "
                  [label]="fieldMappings['physicalHazardActionDescription3']"
                  [requiredField]="
                    requiredFields['physicalHazardActionDescription3']
                  "
                  placeholder="Enter action description"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>

          <!-- Physical Hazard 4 -->
          <ng-container *ngIf="showPhysicalHazardIndex[3]">
            <ion-item-divider class="form-subsection-title">
              <ion-label>
                <p>{{ steps[1].sections?.[3]?.subtitle }}</p>
              </ion-label>
            </ion-item-divider>

            <ion-row
              [class]="
                showPhysicalHazardIndex[4] ? '' : 'ion-no-padding ion-no-margin'
              "
            >
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazard4')!"
                  [label]="fieldMappings['physicalHazard4']"
                  [requiredField]="requiredFields['physicalHazard4']"
                  placeholder="Enter physical hazard"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazardAction4')!"
                  [label]="fieldMappings['physicalHazardAction4']"
                  [requiredField]="requiredFields['physicalHazardAction4']"
                  placeholder="Enter action taken"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="
                    hazardsFormGroup.get('physicalHazardActionDescription4')!
                  "
                  [label]="fieldMappings['physicalHazardActionDescription4']"
                  [requiredField]="
                    requiredFields['physicalHazardActionDescription4']
                  "
                  placeholder="Enter action description"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>

          <!-- Physical Hazard 5 -->
          <ng-container *ngIf="showPhysicalHazardIndex[4]">
            <ion-item-divider class="form-subsection-title">
              <ion-label>
                <p>{{ steps[1].sections?.[4]?.subtitle }}</p>
              </ion-label>
            </ion-item-divider>
            <ion-row
              class="ion-no-padding ion-no-margin"
            >
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazard5')!"
                  [label]="fieldMappings['physicalHazard5']"
                  [requiredField]="requiredFields['physicalHazard5']"
                  placeholder="Enter physical hazard"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="hazardsFormGroup.get('physicalHazardAction5')!"
                  [label]="fieldMappings['physicalHazardAction5']"
                  [requiredField]="requiredFields['physicalHazardAction5']"
                  placeholder="Enter action taken"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="4">
                <app-text-input
                  [templateFormGroup]="hazardsFormGroup"
                  [control]="
                    hazardsFormGroup.get('physicalHazardActionDescription5')!
                  "
                  [label]="fieldMappings['physicalHazardActionDescription5']"
                  [requiredField]="
                    requiredFields['physicalHazardActionDescription5']
                  "
                  placeholder="Enter action description"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>

          <!-- Add Hazard Button -->
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center" >
            <ion-button (click)="addPhysicalHazard()" fill="clear"
            [title]="showAddPhysicalHazardButton ? 'Add Physical Hazard' : ''"
            [disabled]="!showAddPhysicalHazardButton"
              ><ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddPhysicalHazardButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
          </ion-row>
        </ion-grid>

        <!-- Atmospheric Hazards Section -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{steps[1].sections?.[5]?.title}}</h2>
            <p>{{steps[1].sections?.[5]?.description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <!-- Atmospheric Hazard 1 -->
          <ion-row [class]="showAtmosphericHazardIndex[1] ? '' : 'ion-no-padding ion-no-margin'">
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('atmosphericHazard1')!"
                [label]="fieldMappings['atmosphericHazard1']"
                [requiredField]="requiredFields['atmosphericHazard1']"
                placeholder="Enter atmospheric hazard"
              >
              </app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('atmosphericHazardAction1')!"
                [label]="fieldMappings['atmosphericHazardAction1']"
                [requiredField]="requiredFields['atmosphericHazardAction1']"
                placeholder="Enter action taken"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="
                  hazardsFormGroup.get('atmosphericHazardActionDescription1')!
                "
                [label]="fieldMappings['atmosphericHazardActionDescription1']"
                [requiredField]="
                  requiredFields['atmosphericHazardActionDescription1']
                "
                placeholder="Enter action description"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Atmospheric Hazard 2 -->
          <ng-container *ngIf="showAtmosphericHazardIndex[1]">
          <ion-item-divider class="form-subsection-title">
            <ion-label>
              <p>{{steps[1].sections?.[6]?.subtitle}}</p>
            </ion-label>
          </ion-item-divider>
          <ion-row class="ion-no-padding ion-no-margin">
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('atmosphericHazard2')!"
                [label]="fieldMappings['atmosphericHazard2']"
                [requiredField]="requiredFields['atmosphericHazard2']"
                placeholder="Enter atmospheric hazard"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="hazardsFormGroup.get('atmosphericHazardAction2')!"
                [label]="fieldMappings['atmosphericHazardAction2']"
                [requiredField]="requiredFields['atmosphericHazardAction2']"
                placeholder="Enter action taken"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="hazardsFormGroup"
                [control]="
                  hazardsFormGroup.get('atmosphericHazardActionDescription2')!
                "
                [label]="fieldMappings['atmosphericHazardActionDescription2']"
                [requiredField]="
                  requiredFields['atmosphericHazardActionDescription2']
                "
                placeholder="Enter action description"
              >
              </app-text-input>
              </ion-col>
            </ion-row>
          </ng-container>
         
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
            <ion-button (click)="addAtmosphericHazard()" fill="clear"
            [title]="showAddAtmosphericHazardButton ? 'Add Atmospheric Hazard' : ''"
            [disabled]="!showAddAtmosphericHazardButton"
            >
              <ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddAtmosphericHazardButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Ventilation & Monitoring Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
      <ion-list>
        <!-- Ventilation Section -->
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <!-- Forced Air Ventilation Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{steps[2].sections?.[0]?.title}}</h2>
              <p>{{steps[2].sections?.[0]?.description}}</p>
            </ion-label>
          </ion-item-divider>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="
                  ventilationFormGroup.get('forcedAirVentilationRequired')!
                "
                [label]="fieldMappings['forcedAirVentilationRequired']"
                [requiredField]="requiredFields['forcedAirVentilationRequired']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Conditional Ventilation Type Section -->
          <ion-row
            *ngIf="
              ventilationFormGroup.get('forcedAirVentilationRequired')
                ?.value === 'Yes'
            "
          >
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('ventilationType')!"
                [label]="fieldMappings['ventilationType']"
                [requiredField]="requiredFields['ventilationType']"
                [options]="[
                  fieldMappings['ventilationTypeOption1'],
                  fieldMappings['ventilationTypeOption2']
                ]"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <ion-row>
            <ion-col>
              <app-radio-group-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('multipleUnitsRequired')!"
                [label]="fieldMappings['multipleUnitsRequired']"
                [requiredField]="requiredFields['multipleUnitsRequired']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Air Monitoring Section -->

        <ion-grid>
          <!-- Oxygen Monitoring Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{steps[2].sections?.[1]?.title}}</h2>
              <p>{{steps[2].sections?.[1]?.description}}</p>
            </ion-label>
          </ion-item-divider>
          <ion-row class="ion-no-padding ion-no-margin">
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('oxygenResult1')!"
                [label]="fieldMappings['oxygenResult1']"
                [requiredField]="requiredFields['oxygenResult1']"
                placeholder="Enter oxygen result (%)"
              >
              </app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showOxygenResultIndex[1]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('oxygenResult2')!"
                [label]="fieldMappings['oxygenResult2']"
                [requiredField]="requiredFields['oxygenResult2']"
                placeholder="Enter oxygen result (%)"
              >
              </app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showOxygenResultIndex[2]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('oxygenResult3')!"
                [label]="fieldMappings['oxygenResult3']"
                [requiredField]="requiredFields['oxygenResult3']"
                placeholder="Enter oxygen result (%)"
              >
              </app-text-input>
            </ion-col>

    
          </ion-row>
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
            <ion-button (click)="addOxygenResult()" fill="clear"
            [title]="showAddOxygenResultButton ? 'Add Oxygen Result' : ''"
            [disabled]="!showAddOxygenResultButton"
            >
              <ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddOxygenResultButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
          </ion-row>

          <!-- Hydrogen Sulfide Monitoring Section -->

          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[2].sections?.[2]?.title }}</h2>
              <p>{{ steps[2].sections?.[2]?.description }}</p>
            </ion-label>
          </ion-item-divider>
          <ion-row class="ion-no-padding ion-no-margin">
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('hydrogenSulfideResult1')!"
                [label]="fieldMappings['hydrogenSulfideResult1']"
                [requiredField]="requiredFields['hydrogenSulfideResult1']"
                placeholder="Enter hydrogen sulfide result (ppm)"
              ></app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showHydrogenSulfideResultIndex[1]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('hydrogenSulfideResult2')!"
                [label]="fieldMappings['hydrogenSulfideResult2']"
                [requiredField]="requiredFields['hydrogenSulfideResult2']"
                placeholder="Enter hydrogen sulfide result (ppm)"
              ></app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showHydrogenSulfideResultIndex[2]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('hydrogenSulfideResult3')!"
                [label]="fieldMappings['hydrogenSulfideResult3']"
                [requiredField]="requiredFields['hydrogenSulfideResult3']"
                placeholder="Enter hydrogen sulfide result (ppm)"
              ></app-text-input>
            </ion-col>
          </ion-row>
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
            <ion-button (click)="addHydrogenSulfideResult()" fill="clear"
            [title]="showAddHydrogenSulfideResultButton ? 'Add Hydrogen Sulfide Result' : ''"
            [disabled]="!showAddHydrogenSulfideResultButton"
            >
              <ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddHydrogenSulfideResultButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
          </ion-row>

          <!-- Carbon Monoxide Monitoring Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[2].sections?.[3]?.title }}</h2>
              <p>{{ steps[2].sections?.[3]?.description }}</p>
            </ion-label>
          </ion-item-divider>

          <ion-row class="ion-no-padding ion-no-margin">
            <ion-col size="12" size-md="4" >
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('carbonMonoxideResult1')!"
                [label]="fieldMappings['carbonMonoxideResult1']"
                [requiredField]="requiredFields['carbonMonoxideResult1']"
                placeholder="Enter carbon monoxide result (ppm)"
              ></app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showCarbonMonoxideResultIndex[1]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('carbonMonoxideResult2')!"
                [label]="fieldMappings['carbonMonoxideResult2']"
                [requiredField]="requiredFields['carbonMonoxideResult2']"
                placeholder="Enter carbon monoxide result (ppm)"
              ></app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showCarbonMonoxideResultIndex[2]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('carbonMonoxideResult3')!"
                [label]="fieldMappings['carbonMonoxideResult3']"
                [requiredField]="requiredFields['carbonMonoxideResult3']"
                placeholder="Enter carbon monoxide result (ppm)"
              ></app-text-input>
            </ion-col>
          </ion-row>
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
            <ion-button (click)="addCarbonMonoxideResult()" fill="clear"
            [title]="showAddCarbonMonoxideResultButton ? 'Add Carbon Monoxide Result' : ''"
            [disabled]="!showAddCarbonMonoxideResultButton"
            >
              <ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddCarbonMonoxideResultButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
          </ion-row>

          <!-- Lower Explosive Limit Monitoring Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[2].sections?.[4]?.title }}</h2>
              <p>{{ steps[2].sections?.[4]?.description }}</p>
            </ion-label>
          </ion-item-divider>

          <ion-row class="ion-no-padding ion-no-margin">
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="
                  ventilationFormGroup.get('lowerExplosiveLimitResult1')!
                "
                [label]="fieldMappings['lowerExplosiveLimitResult1']"
                [requiredField]="requiredFields['lowerExplosiveLimitResult1']"
                placeholder="Enter lower explosive limit result (%)"
              ></app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showLowerExplosiveLimitResultIndex[1]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="
                  ventilationFormGroup.get('lowerExplosiveLimitResult2')!
                "
                [label]="fieldMappings['lowerExplosiveLimitResult2']"
                [requiredField]="requiredFields['lowerExplosiveLimitResult2']"
                placeholder="Enter lower explosive limit result (%)"
              ></app-text-input>
            </ion-col>

            <ion-col size="12" size-md="4" *ngIf="showLowerExplosiveLimitResultIndex[2]">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="
                  ventilationFormGroup.get('lowerExplosiveLimitResult3')!
                "
                [label]="fieldMappings['lowerExplosiveLimitResult3']"
                [requiredField]="requiredFields['lowerExplosiveLimitResult3']"
                placeholder="Enter lower explosive limit result (%)"
              ></app-text-input>
            </ion-col>
          </ion-row>
          <ion-row class="ion-no-padding ion-no-margin ion-align-items-center">
            <ion-button (click)="addLowerExplosiveLimitResult()" fill="clear"
            [title]="showAddLowerExplosiveLimitResultButton ? 'Add Lower Explosive Limit Result' : ''"
            [disabled]="!showAddLowerExplosiveLimitResultButton"
            >
              <ion-icon
                name="add-circle-outline"
                slot="icon-only"
              ></ion-icon>
            </ion-button>
            <ion-text *ngIf="!showAddLowerExplosiveLimitResultButton" class="add-other-button-text"
              >Maximum Reached</ion-text
            >
          </ion-row>
        </ion-grid>

        <!-- 4 Gas Field Section -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{steps[2].sections?.[5]?.title}}</h2>
            <p>{{steps[2].sections?.[5]?.description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('gasDetectorModel')!"
                [label]="fieldMappings['gasDetectorModel']"
                [requiredField]="requiredFields['gasDetectorModel']"
                placeholder="Enter gas detector model"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="ventilationFormGroup"
                [control]="ventilationFormGroup.get('calibrationDaysLeft')!"
                [label]="fieldMappings['calibrationDaysLeft']"
                [requiredField]="requiredFields['calibrationDaysLeft']"
                placeholder="Enter days left"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Additional Information Step -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
      <ion-list>
        <!-- Additional Notes Section -->
        <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col>
              <app-textarea-input
                [templateFormGroup]="additionalFormGroup"
                [control]="additionalFormGroup.get('additionalNotes')!"
                [label]="fieldMappings['additionalNotes']"
                [requiredField]="requiredFields['additionalNotes']"
                [rows]="4"
                placeholder="Enter additional notes about the space and entry, including whether evacuation was necessary"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>

        <!-- Responsible Person Section -->
        <ion-item-divider class="form-section-title">
          <ion-label>
            <h2>{{steps[3].sections?.[1]?.title}}</h2>
            <p>{{steps[3].sections?.[1]?.description}}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="additionalFormGroup"
                [control]="additionalFormGroup.get('responsiblePersonName')!"
                [label]="fieldMappings['responsiblePersonName']"
                [requiredField]="requiredFields['responsiblePersonName']"
                placeholder="Enter responsible person name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="additionalFormGroup"
                [control]="
                  additionalFormGroup.get('responsiblePersonJobTitle')!
                "
                [label]="fieldMappings['responsiblePersonJobTitle']"
                [requiredField]="requiredFields['responsiblePersonJobTitle']"
                placeholder="Enter job title"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Signature -->
          <ion-row
            *ngFor="let signature of formAttachmentRequirements.signatures"
          >
            <app-signature-section
              [signatureInfo]="signature"
            ></app-signature-section>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout>
